import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Archive, Copy, Eye, FileQuestion, Pencil, Briefcase, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { confirmAction } from "@/components/app/Confirm";
import { api, get, errMsg } from "@/lib/api";
import { STATUS_STYLE } from "@/lib/library";
import { UseForJobDialog } from "@/pages/assessments/library/UseForJobDialog";
import { deleteItem, plural, TrashButton } from "@/lib/deleteItem";

function Card({ t, staff, i, onChanged, onUse }) {
  const nav = useNavigate();
  const [label, cls] = STATUS_STYLE[t.status];
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const archive = async () => {
    if (await confirmAction({ title: "Archive this assessment?", body: "It will be hidden from the library. Candidates who already took it keep their results.", entity: t.name, confirmLabel: "Archive" }))
      act(() => api.post(`/assessment-library/${t.id}/archive`), "Archived");
  };
  const remove = async () => {
    let imp = { jobs: 0, invites: 0 };
    try { imp = await get(`/assessment-library/${t.id}/delete-impact`); } catch (e) { toast.error(errMsg(e)); return; }
    deleteItem({ title: "Delete this assessment?", entity: t.name, url: `/assessment-library/${t.id}`, success: `Deleted "${t.name}"`, onDone: onChanged,
      body: `${imp.jobs ? `It's attached to ${plural(imp.jobs, "job")}, which will be left without an assessment. ` : ""}${imp.invites ? `${plural(imp.invites, "invite")} already sent keep their own copy and results. ` : ""}All its versions and questions are deleted.` });
  };
  return (
    <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      className="panel flex flex-col gap-3 p-5" data-testid={`library-card-${t.id}`}>
      <div className="flex items-start gap-2">
        <h3 className="flex-1 text-base font-semibold">{t.name}</h3>
        <span className={`rounded-full border px-2 py-0.5 text-xs ${cls}`} data-testid={`library-status-${t.id}`}>{label}{t.version > 1 ? ` · v${t.version}` : ""}</span>
      </div>
      {t.description && <p className="text-sm text-ink-secondary">{t.description}</p>}
      <p className="text-sm text-ink-secondary">{t.section_titles.join(" · ") || "No sections yet"}</p>
      {t.warnings?.length > 0 && <p className="text-xs text-caution" data-testid={`library-warnings-${t.id}`}>To fix: {t.warnings.join("; ")}</p>}
      {t.jobs.length > 0 && <p className="text-xs text-ink-secondary">Used by: {t.jobs.map((j) => j.title).join(", ")}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
        <span className="inline-flex items-center gap-1"><Clock size={13} /> about {t.minutes} min</span>
        <span>{t.questions} questions</span>
        <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {t.jobs.length ? `Used by ${t.jobs.length} job${t.jobs.length > 1 ? "s" : ""}` : "Not used yet"}</span>
        <span>Edited {new Date(t.updated_at).toLocaleDateString()}</span>
      </div>
      <div className="mt-auto flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={() => nav(`/app/assessments/${t.id}?step=preview`)} data-testid={`library-preview-${t.id}`}><Eye size={14} /> Preview</Button>
        {staff && <>
          <Button size="sm" variant="outline" onClick={() => nav(`/app/assessments/${t.id}`)} data-testid={`library-edit-${t.id}`}><Pencil size={14} /> Edit</Button>
          <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/assessment-library/${t.id}/duplicate`), "Copy created")} data-testid={`library-duplicate-${t.id}`}><Copy size={14} /> Duplicate</Button>
          <Button size="sm" onClick={() => onUse(t)} disabled={!!t.warnings?.length} title={t.warnings?.length ? "Fix the items listed above first" : undefined} data-testid={`library-use-${t.id}`}>Use for a job</Button>
          <Button size="sm" variant="ghost" onClick={archive} aria-label={`Archive ${t.name}`} data-testid={`library-archive-${t.id}`}><Archive size={14} /></Button>
          <TrashButton label={`Delete ${t.name}`} testId={`delete-template-${t.id}`} onClick={remove} />
        </>}
      </div>
    </motion.article>
  );
}

export function LibraryGrid({ staff }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [use, setUse] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["assessment-library"], queryFn: () => get("/assessment-library") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["assessment-library"] });
  if (isLoading) return <SkeletonRows rows={3} />;
  if (!data?.length) return <EmptyState icon={FileQuestion} title="No assessments yet" text="Create one from scratch or start from a template." testId="library-empty"
    action={staff && <Button onClick={() => nav("/app/assessments/new")} data-testid="library-empty-create">Create assessment</Button>} />;
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="library-grid" data-tour="assessment-library">
        {data.map((t, i) => <Card key={t.id} t={t} i={i} staff={staff} onChanged={refresh} onUse={setUse} />)}
      </div>
      <UseForJobDialog template={use} onClose={() => setUse(null)} onDone={refresh} />
    </>
  );
}
